<template>
    <div>
        <page-title :title="$t('thematicArea.title')" :showTitle="false"></page-title>
        <div class="card col-12 mx-auto mt-2 px-0">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">{{ $t('thematicArea.title') }}</div>
            </div>

            <div class="card-header" v-if="showSettingsBtn && showNewButton">
                <button type="button" class="btn btn-primary btn-lg"
                    v-if="showNewButton && claims.thematicAreasRegister.create"
                    @click="goToCreate">
                    {{$t('buttons.create.btnText')}}
                </button>

                <button type="button"
                        class="btn btn-primary btn-lg"
                        v-if="showExportTemplateButton && claims.thematicAreasRegister.read"
                        @click="goToExportTemplate">
                    {{$t('buttons.exportTemplate.btnText')}}
                </button>
                <label v-if="showExportTemplateButton && claims.thematicAreasRegister.read">
                    <input type="checkbox" id="exportWithData" class="checkbox" />
                    <span>{{$t('checkboxes.exportWithData.cbText')}}</span>
                </label>

                <form v-if="claims.thematicAreasRegister.create" enctype="multipart/form-data" method="post" id="importForm" class="inlineForm">
                    <div class="inlineForm">
                        <button type="button"
                                class="btn btn-primary btn-lg"
                                v-if="showImportFromExcelButton"
                                @click="selectImportFiles">
                            {{$t('buttons.importFromExcel.btnText')}}
                        </button>
                        <input type="file" id="importFile" class="selectFile" @change="goToImportFromExcel" />
                    </div>
                </form>

                <i class="fas fa-cogs pointer fa-2x float-right text-success"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @deleteGridSettings="deleteGridSettings"
                                    @userSettingsChanged="userSettingsChanged"></grid-user-settings>
            </div>

            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="form-inline col-6"></div>

                    <div class="input-group">
                        <input v-model="searchQuery"
                               v-on:keyup.enter.stop="massSearch"
                               v-on:keyup.esc.stop="clearFilters"
                               type="text"
                               class="form-control"
                               aria-label="Mass search input"
                               aria-describedby="massSearch" />
                        <button class="btn btn-secondary" @click.stop="massSearch">
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button class="btn btn-secondary" @click.stop="clearFilters">
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size="defaultGridRows"
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError"></kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :toolbar="toolbar"
                            :excel="gridOptions.excel"
                            :dataBound="dataBound">
                    <kendo-grid-column
                        :template="btnsTemplate"
                        v-if="showBtns"
                        field="btnsColumn"
                        :title="$t('actions')"
                        :reorderable="false"
                        :sortable="false"
                        :filterable="false"
                        :lockable="false"
                        :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'id'" :title="$t('thematicArea.id')"></kendo-grid-column>
                    <kendo-grid-column :field="'name'" :title="$t('thematicArea.name')"></kendo-grid-column>
                    <kendo-grid-column :field="'legalArea'" :title="$t('thematicArea.legalArea')"></kendo-grid-column>
                </kendo-grid>
            </div>
        </div>
        <JsonFormatter
            v-model="dataReferences"
            :show.sync="showDataReferences"
            :titleKey="'buttons.links.tooltipText'"
            @onClose="onDataReferencesClose"
        >
        </JsonFormatter>
    </div>
</template>

<script>
    import Vue from 'vue';
    import store from '@/store/store';
    import GridUserSettings from '@/components/grid/UserSettings';
    import BtnsTemplate from '@/components/grid/StandardBtnsTemplate';
    import PageTitle from '@/components/common/PageTitle';
    import personalizationService from '@/services/personalization.service';
    import { KendoGridPageSize } from '@/models/enums';
    import { mapGetters } from 'vuex';
    import dataReferencesService from "@/services/dataReferences.service";
    import JsonFormatter from "@/components/common/json.formatter.modal.vue";

    export default {
        props: {
            showHideButton: {
                default: false,
                type: Boolean
            },
            showPinButton: {
                default: true,
                type: Boolean
            },
            showNewButton: {
                default: true,
                type: Boolean
            },
            showExportTemplateButton: {
                default: true,
                type: Boolean
            },
            showImportFromExcelButton: {
                default: true,
                type: Boolean
            },
            showResizeIcon: {
                default: false,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            }
        },
        components: {
            PageTitle,
            'grid-user-settings': GridUserSettings,
            JsonFormatter
        },
        data() {
            return {
                componentName: 'ThematicAreasRegisterGrid',
                titleKey: 'thematicArea.title',
                searchQuery: '',
                defaultGridRows: this.$store.getters.defaultGridRows,
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                schemaModelFields: {
                    name: {
                        name: 'name',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'thematicArea.name'
                    },
                    id: {
                        name: 'id',
                        type: 'number',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'thematicArea.id'
                    },
                    legalArea: {
                        name: 'legalArea',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'thematicArea.legalArea'
                    }
                },
                rawHtml: '',
                transportSettings: {
                    read: {
                        url: `${store.getters.baseUrl}/api/thematicArea/list`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    editable: {
                        mode: 'popup',
                        window: {
                            title: this.$t('person.gridTitle'),
                            animation: true
                        },
                        move: true
                    },
                    excel: {
                        fileName: 'ThematicAreas.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: 'ThematicAreas.pdf',
                        title: this.$t(this.titleKey),
                        subject: this.$t(this.titleKey),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                },
                showUserSettingsForm: false,
                dataReferences: "",
                showDataReferences: false,
            };
        },
        methods: {
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration
                });
            },
            goToCreate(event) {
                this.$router.push('/registers/thematicArea/create');
            },
            goToExportTemplate() {
                var $exportCheckbox = $('#exportWithData');
                var isChecked = $exportCheckbox.is(':checked');

                this.$http
                    .get('/api/thematicArea/exportTemplate/' + isChecked, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'ThematicAreas.xlsx');
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            deleteBtnClick(args) {
                kendo.confirm(`${this.$t('buttons.delete.confirmationText1', { name: args.name })}`).then(
                    function () {
                        this.deleteThematicArea(args.id);
                    }.bind(this)
                );
            },
            deleteThematicArea(id) {
                this.$http
                    .delete('/api/thematicArea/', { params: { id: id } })
                    .then(response => {
                        this.refresh();
                        let statusCode = Number(this.$helpers.responseStatusCode(response));
                        this.$vs.notify({
                            title: this.$t('buttons.delete.action'),
                            text: this.$helpers.responseMessage(response),
                            color: statusCode === 240 ? 'warning' : 'success',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            refresh() {
                let gr = this.$refs.grid;
                if (gr) {
                    gr.kendoWidget().dataSource.read();
                }
            },
            selectImportFiles() {
                var dialog = $('#importFile');
                dialog.trigger('click');
            },
            goToImportFromExcel(args) {
                var $input = $('#importFile')[0];
                var files = $input.files;
                if (files && files.length > 0) {
                    var filename = files[0].name;
                    var file = files[0];

                    // clear the selected file so that the change event triggers when the same file is selected
                    $input.value = null;

                    var formData = new FormData();
                    formData.append('FileName', filename);
                    formData.append('FileContent', file);

                    this.$http
                        .post('/api/thematicArea/import', formData)
                        .then(result => {
                            this.$refs.grid.kendoWidget().dataSource.read();
                            this.$vs.notify({
                                title: this.$t('notifications.success'),
                                text: result.data,
                                position: 'bottom-right',
                                color: 'success'
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('notificationsPopup.registerImport.errorTitle'),
                                text: this.$t(error.response.data.msg) + (error.response.data.detailMsg || ''),
                                color: 'danger',
                                time: this.$store.getters.notificationDuration
                            });
                        });
                }
            },
            goToEdit(args) {
                this.$router.push(`/registers/thematicArea/${args.id}/edit`);
            },
            goToDetails(args) {
                this.$router.push(`/registers/thematicArea/${args.id}/details`);
            },
            pdfExport(args) {
                this.$http
                    .get('/api/thematicArea/pdfExport/' + args.id, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'ThematicArea.pdf');
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            excelExport(args) {
                this.$http
                    .get('/api/thematicArea/excelExport/' + args.id, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'ThematicArea.xlsx');
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            dataBound(e) {
                if (!this.customSettingsLoaded) {
                    this.loadGridSettings();
                }
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) };
                } else {
                    this.saveState();
                    return kendo.stringify(options);
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            toolbarTemplate: function () {
                let templateHtml =
                    '<span>' + '<a class="k-pager-refresh k-link k-button" title="Refresh"><span class="k-icon k-i-reload"></span></a>' + '</span>';

                return kendo.template(templateHtml);
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        parentComponent: this.$refs.grid,
                        btns: [
                            {
                                name: 'edit',
                                text: '',
                                tooltip: this.$t('buttons.edit.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'fas fa-edit',
                                show: this.claims.thematicAreasRegister.update,
                                clickHandler: this.goToEdit
                            },
                            {
                                name: 'details',
                                text: '',
                                tooltip: this.$t('buttons.details.tooltipText'),
                                class: 'btn btn-info',
                                icon: 'fas fa-eye',
                                show: this.claims.thematicAreasRegister.read,
                                clickHandler: this.goToDetails
                            },
                            {
                                name: 'delete',
                                text: '',
                                tooltip: this.$t('buttons.delete.tooltipText'),
                                class: 'btn btn-danger',
                                icon: 'fas fa-times',
                                show: this.claims.thematicAreasRegister.delete,
                                clickHandler: this.deleteBtnClick
                            },
                            //     {
                            //     name: "pdfExport",
                            //     text: "",
                            //     tooltip: this.$t('buttons.pdfExport.tooltipText'),
                            //     class: 'btn btn-danger',
                            //     icon: "far fa-file-pdf",
                            //     show: this.claims.thematicAreasRegister.read,
                            //     clickHandler: this.pdfExport
                            // },
                            {
                                name: 'excelExport',
                                text: '',
                                tooltip: this.$t('buttons.excelExport.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'far fa-file-excel',
                                show: this.claims.thematicAreasRegister.read,
                                clickHandler: this.excelExport
                            },
                            {
                                name: "checkLinks",
                                text: "",
                                tooltip: this.$t("buttons.links.btnText"),
                                class: "btn btn-info",
                                icon: "fa fa-link",
                                show: this.claims.dataReferences.read,
                                clickHandler: this.dataReferencesBtnClick,
                            },
                        ]
                    }
                };
            },
            gridSettingsChanged(componentName, name, value) {
                this.setColumnVisibilitySettings();
            },
            loadColumnsVisibilitySettings() {
                return this.$http
                    .get('/api/personalization/' + this.componentName + '/getColumnVisibility')
                    .then(response => {
                        if (response.data.settings === null) {
                            return;
                        }

                        let settings = [];
                        for (var i = 0; i < response.data.settings.length; i++) {
                            settings.push(response.data.settings[i].name);
                        }
                        for (const key in this.schemaModelFields) {
                            if (this.schemaModelFields.hasOwnProperty(key)) {
                                this.schemaModelFields[key].visible = settings.includes(key);
                            }
                        }

                        this.setColumnVisibilitySettings();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            setColumnVisibilitySettings() {
                var grid = this.$refs.grid.kendoWidget();
                var columns = grid.columns;

                // restore columns visibility state using the stored values
                for (var i = 0; i < columns.length; i++) {
                    var column = columns[i];
                    if (
                        !column.field ||
                        !this.schemaModelFields.hasOwnProperty(column.field) ||
                        !this.schemaModelFields[column.field].hasOwnProperty('visible')
                    ) {
                        continue;
                    }

                    if (this.schemaModelFields[column.field].visible) {
                        grid.showColumn(column);
                    } else {
                        grid.hideColumn(column);
                    }
                }
            },
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();
                const grid = this.$refs.grid.kendoWidget();
                if (gridSettings && grid) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            });
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService.mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size !== grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }
                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (pageSizeChanged === true) {
                            if (userSettings.pageable.size === KendoGridPageSize.All) {
                                grid.dataSource.pageSize(this.$refs['datasource1'].kendoDataSource.total());
                            } else {
                                grid.dataSource.pageSize(userSettings.pageable.size);
                            }
                            grid.refresh();
                        }
                    } catch (e) { }
                }
            },
            saveState() {
                personalizationService.saveState(this.componentName, this.getGridOptions());
            },
            loadState: function () {
                let stateSettings = personalizationService.getState(this.componentName);
                let options = personalizationService.mergeStateSettings(this.getGridOptions(), stateSettings);
                if (options) {
                    let grid = this.$refs.grid.kendoWidget();
                    if (grid) {
                        grid.setOptions(options);
                    }
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            clearFilters() {
                personalizationService.claerState(this.componentName);

                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            dataReferencesBtnClick(args) {
                if (!this.claims.dataReferences.read) {
                    return this.$router.push(`/unauthorized`);
                }

                return this.getDataReferences(args.id);
            },
            getDataReferences(id) {
                dataReferencesService
                    .checkReferences("Registers", "ThematicArea", id, this.dataReferencesListCount, true)
                    .then((response) => {
                        if (response.data) {
                            this.dataReferences = response.data;
                        }

                        this.showDataReferences = true;
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            },
            onDataReferencesClose() {
                this.dataReferences = "";
                this.showDataReferences = false;
            },
        },
        mounted: function () {
            var container = $(this.$el);
            container.find('[data-toggle="tooltip"]').tooltip({ container });
        },
        computed: {
            ...mapGetters(['claims', 'dataReferencesListCount']),
            token: function () {
                return store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }];
            },
        }
    };
</script>

<style scoped>
    .btn {
        margin-right: 10px;
        margin-left: 5px;
    }

    .checkbox {
        margin-right: 5px;
    }

    .selectFile {
        display: none;
    }

    .inlineForm {
        display: inline;
    }
</style>
